// useRfi3Tabs.js
import { useEffect, useCallback } from 'react'
import { useRfi3Context } from './Rfi3Context'

export const useRfi3Tabs = () => {
  const { state, dispatch } = useRfi3Context()
  const { rfi3Template, activeTab, activeRFI3Section, errorCache } = state

  const setActiveTab = useCallback(
    (tab) => {
      dispatch({ type: 'SET_ACTIVE_TAB', payload: tab })
    },
    [dispatch],
  )

  const setActiveRfi3Section = useCallback(
    (section) => {
      dispatch({ type: 'SET_ACTIVE_SECTION', payload: section })
    },
    [dispatch],
  )

  const setActiveSectionOfTab = useCallback(
    (selectedTab, sectionIndex = 0) => {
      if (
        !selectedTab ||
        !selectedTab.sections ||
        !selectedTab.sections[sectionIndex]
      ) {
        return
      }
      const section = selectedTab.sections[sectionIndex]
      dispatch({
        type: 'SET_ACTIVE_SECTION',
        payload: {
          index: sectionIndex,
          uid: section.uid,
          name: section.section_name,
        },
      })
    },
    [dispatch],
  )

  const setTabByIndex = useCallback(
    (template, tabIndex = 0) => {
      const tabs = template?.dynamic_sections?.tabs || []
      if (!tabs[tabIndex]) return
      const tab = tabs[tabIndex]
      dispatch({
        type: 'SET_ACTIVE_TAB',
        payload: { index: tabIndex, uid: tab.uid, name: tab.tab_name },
      })
    },
    [dispatch],
  )

  useEffect(() => {
    const tabs = rfi3Template?.dynamic_sections?.tabs || []
    if (!tabs.length) return

    if (!activeTab.name) {
      const firstTabIndex = 0
      const firstTab = tabs[firstTabIndex]
      setTabByIndex(rfi3Template, firstTabIndex)
      setActiveSectionOfTab(firstTab, 0)
      return
    }

    const activeTabObject = tabs[activeTab.index]
    if (activeTabObject) {
      setActiveSectionOfTab(activeTabObject, 0)
    }
  }, [
    rfi3Template,
    activeTab.name,
    activeTab.index,
    setTabByIndex,
    setActiveSectionOfTab,
  ])

  const gotoPreviousSection = useCallback(() => {
    const tabs = rfi3Template?.dynamic_sections?.tabs || []
    const currentTab = tabs[activeTab.index]
    if (!currentTab) return

    const prevIndex = activeRFI3Section.index - 1
    if (prevIndex < 0) return

    const prevSection = currentTab.sections[prevIndex]
    if (!prevSection) return

    dispatch({
      type: 'SET_ACTIVE_SECTION',
      payload: {
        index: prevIndex,
        uid: prevSection.uid,
        name: prevSection.section_name,
      },
    })
  }, [dispatch, rfi3Template, activeTab.index, activeRFI3Section.index])

  const gotoNextSection = useCallback(() => {
    const tabs = rfi3Template?.dynamic_sections?.tabs || []
    const currentTab = tabs[activeTab.index]
    if (!currentTab) return

    const nextIndex = activeRFI3Section.index + 1
    const nextSection = currentTab.sections[nextIndex]
    if (!nextSection) return

    dispatch({
      type: 'SET_ACTIVE_SECTION',
      payload: {
        index: nextIndex,
        uid: nextSection.uid,
        name: nextSection.section_name,
      },
    })
  }, [dispatch, rfi3Template, activeTab.index, activeRFI3Section.index])

  const getTabIdByName = useCallback(
    (tabName) => {
      const tabs = rfi3Template?.dynamic_sections?.tabs || []
      const tab = tabs.find((t) => t.tab_name === tabName)
      return tab?.uid
    },
    [rfi3Template],
  )

  const isTabErrored = useCallback(
    (tabName) => {
      const tabId = getTabIdByName(tabName)
      return !!(tabId && errorCache?.tab?.includes(tabId))
    },
    [getTabIdByName, errorCache],
  )

  return {
    activeTab,
    activeRFI3Section,
    setActiveTab,
    setActiveRfi3Section,
    setActiveSectionOfTab,
    setTabByIndex,
    gotoPreviousSection,
    gotoNextSection,
    getTabIdByName,
    isTabErrored,
  }
}
